import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { authApi } from '../../services/api'
import { User, RegisterData } from '../../types/auth'
import Cookies from 'js-cookie'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  token: Cookies.get('token') || null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

/** Login step 2: verify OTP → store token */
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, otp_code }: { email: string; otp_code: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.verifyOtp(email, otp_code)
      Cookies.set('token', response.access_token, { expires: 7 })
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Login failed')
    }
  }
)

/** Google OAuth → store token */
export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (
    payload: string | { code?: string; id_token?: string; redirect_uri?: string },
    { rejectWithValue }
  ) => {
    try {
      const requestPayload = typeof payload === 'string' ? { id_token: payload } : payload
      const response = await authApi.googleLogin(requestPayload)
      Cookies.set('token', response.access_token, { expires: 7 })
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Google login failed')
    }
  }
)

/** Registration step 1: submit form → backend creates user + sends OTP */
export const register = createAsyncThunk(
  'auth/register',
  async (registerData: RegisterData, { rejectWithValue }) => {
    try {
      return await authApi.register(registerData)
    } catch (error: any) {
      const detail = error.response?.data?.detail
      const msg = Array.isArray(detail)
        ? detail.map((e: any) => `${String(e.loc?.at(-1) ?? '')}: ${e.msg}`).join(' · ')
        : (typeof detail === 'string' ? detail : 'Registration failed')
      return rejectWithValue(msg)
    }
  }
)

/** Registration step 2: verify OTP → store token, continue onboarding */
export const verifyRegistrationOtp = createAsyncThunk(
  'auth/verifyRegistrationOtp',
  async ({ email, otp_code }: { email: string; otp_code: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.verifyOtp(email, otp_code)
      Cookies.set('token', response.access_token, { expires: 7 })
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'OTP verification failed')
    }
  }
)

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = Cookies.get('token')
      if (!token) throw new Error('No token found')
      return await authApi.getCurrentUser()
    } catch (error: any) {
      Cookies.remove('token')
      return rejectWithValue('Authentication failed')
    }
  }
)

export const logout = createAsyncThunk('auth/logout', async () => {
  Cookies.remove('token')
  return null
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    updateOnboardingStep: (state, action: PayloadAction<string>) => {
      if (state.user) state.user.onboarding_step = action.payload
    },
  },
  extraReducers: (builder) => {
    const loginFulfilled = (state: AuthState, action: any) => {
      state.user = action.payload.user
      state.token = action.payload.access_token
      state.isAuthenticated = true
    }
    const rejected = (state: AuthState, action: any) => {
      state.error = action.payload as string
    }

    builder
      .addCase(login.pending, (state) => { state.error = null })
      .addCase(login.fulfilled, loginFulfilled)
      .addCase(login.rejected, rejected)

      .addCase(googleLogin.pending, (state) => { state.error = null })
      .addCase(googleLogin.fulfilled, loginFulfilled)
      .addCase(googleLogin.rejected, rejected)

      .addCase(register.pending, (state) => { state.error = null })
      .addCase(register.fulfilled, () => {})
      .addCase(register.rejected, rejected)

      .addCase(verifyRegistrationOtp.pending, (state) => { state.error = null })
      .addCase(verifyRegistrationOtp.fulfilled, (state, action) => {
        state.token = action.payload.access_token
        state.user = action.payload.user
        // Not fully authenticated yet — still in onboarding
      })
      .addCase(verifyRegistrationOtp.rejected, rejected)

      .addCase(checkAuth.pending, (state) => { state.isLoading = true })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        // A valid token means the user is authenticated. Onboarding progress
        // controls *where* we route them, not *whether* they're logged in —
        // gating auth on onboarding_step bounced unpaid users to /login on refresh.
        state.isAuthenticated = true
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
      })
  },
})

export const { clearError, setCredentials, updateOnboardingStep, updateUser } = authSlice.actions
export default authSlice.reducer
